// FILE: src/components/OnboardingChecklist.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";

const B = {
  red: "#980000",
  redDark: "#650202",
  orange: "#f15822",
  navy: "#003353",
  steel: "#657899",
  bg: "#f6f7fb",
  white: "#ffffff",
  border: "#e8ebf3",
};

function CheckItem({ done, label, description, href, linkLabel }) {
  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: "12px 0", borderBottom: `1px solid ${B.border}`,
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%", flexShrink: 0, marginTop: 1,
        background: done ? B.red : B.white,
        border: `2px solid ${done ? B.red : B.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {done && <span style={{ color: B.white, fontSize: 12, fontWeight: 900 }}>✓</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 14, fontWeight: 700,
          color: done ? B.steel : B.redDark,
          textDecoration: done ? "line-through" : "none",
        }}>{label}</div>
        {!done && description && (
          <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>{description}</div>
        )}
        {!done && href && (
          <a href={href} style={{
            display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 700,
            color: B.white, background: B.red, borderRadius: 6,
            padding: "4px 10px", textDecoration: "none",
          }}>{linkLabel || "Complete"}</a>
        )}
      </div>
    </div>
  );
}

function Modal({ checks, onClose }) {
  const done = checks.filter(c => c.done).length;
  const total = checks.length;
  const pct = Math.round((done / total) * 100);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.45)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: B.white, borderRadius: 18, width: "100%", maxWidth: 480,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }} onClick={e => e.stopPropagation()}>
        {/* Modal header */}
        <div style={{
          background: B.redDark, padding: "20px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ color: B.white, fontWeight: 800, fontSize: 16 }}>Getting started</div>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginTop: 2 }}>
              {done} of {total} steps complete
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.15)", border: "none", color: B.white,
            borderRadius: 8, width: 32, height: 32, cursor: "pointer",
            fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
          }}>✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: B.border }}>
          <div style={{
            height: "100%", width: `${pct}%`,
            background: `linear-gradient(90deg, ${B.red}, ${B.orange})`,
            transition: "width 0.4s ease",
          }} />
        </div>

        {/* Steps */}
        <div style={{ padding: "4px 24px 16px", maxHeight: "60vh", overflowY: "auto" }}>
          {checks.map(c => <CheckItem key={c.id} {...c} />)}
        </div>
      </div>
    </div>
  );
}

export default function OnboardingChecklist({ user, tenantId, tenantRole }) {
  const [checks, setChecks] = useState(null);
  const [open, setOpen] = useState(false);

  const uid = user?.uid;
  const emailVerified = user?.emailVerified || false;
  const isOwner = tenantRole === "owner" || tenantRole === "hr_admin";
  const isPersonal = !tenantId;

  useEffect(() => {
    if (!uid) return;
    async function load() {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        const data = snap.exists() ? snap.data() : {};
        const profile = data.profile || {};

        const hasName = !!(profile.fullName || data.displayName || user?.displayName || "").trim();
        const hasPhone = !!(profile.phone || data.phone || "").trim();
        const hasMfa = !!(data.mfaEnabled || data.mfa?.enabled);

        let hasAccountManager = false;
        let hasDocument = false;
        let hasTeamMember = false;

        if (isPersonal) {
          try {
            const amSnap = await getDocs(query(collection(db, "accountManagers"), where("uid", "==", uid)));
            hasAccountManager = !amSnap.empty;
          } catch {}
          try {
            const docSnap = await getDocs(query(collection(db, "importantDocuments"), where("uid", "==", uid)));
            hasDocument = !docSnap.empty;
          } catch {}
        }

        if (isOwner && tenantId) {
          try {
            const membersSnap = await getDocs(collection(db, "tenants", tenantId, "members"));
            hasTeamMember = membersSnap.size > 1;
          } catch {}
        }

        let steps = [];
        if (isPersonal) {
          steps = [
            { id: "email", done: emailVerified, label: "Verify your email", description: "Check your inbox for a verification link.", href: "/profile", linkLabel: "Go to profile" },
            { id: "profile", done: hasName && hasPhone, label: "Complete your profile", description: "Add your full name and phone number.", href: "/profile", linkLabel: "Update profile" },
            { id: "manager", done: hasAccountManager, label: "Add an account manager", description: "Choose someone who can manage your account.", href: "/account-managers", linkLabel: "Add manager" },
            { id: "document", done: hasDocument, label: "Upload an important document", description: "Store a will, insurance policy or other key document.", href: "/legal/important-documents", linkLabel: "Upload document" },
            { id: "mfa", done: hasMfa, label: "Enable two-factor authentication", description: "Add an extra layer of security to your account.", href: "/profile", linkLabel: "Set up MFA" },
          ];
        } else if (isOwner) {
          steps = [
            { id: "email", done: emailVerified, label: "Verify your email", description: "Check your inbox for a verification link.", href: "/profile", linkLabel: "Go to profile" },
            { id: "profile", done: hasName && hasPhone, label: "Complete your profile", description: "Add your full name and phone number.", href: "/profile", linkLabel: "Update profile" },
            { id: "team", done: hasTeamMember, label: "Invite your first team member", description: "Add employees to your business workspace.", href: `/tenant/${tenantId}`, linkLabel: "Manage team" },
            { id: "mfa", done: hasMfa, label: "Enable two-factor authentication", description: "Add an extra layer of security to your account.", href: "/profile", linkLabel: "Set up MFA" },
          ];
        } else {
          steps = [
            { id: "email", done: emailVerified, label: "Verify your email", description: "Check your inbox for a verification link.", href: "/profile", linkLabel: "Go to profile" },
            { id: "profile", done: hasName && hasPhone, label: "Complete your profile", description: "Add your full name and phone number.", href: "/profile", linkLabel: "Update profile" },
            { id: "mfa", done: hasMfa, label: "Enable two-factor authentication", description: "Add an extra layer of security to your account.", href: "/profile", linkLabel: "Set up MFA" },
          ];
        }

        setChecks(steps);
      } catch (e) {
        console.warn("OnboardingChecklist load failed", e);
      }
    }
    load();
  }, [uid, emailVerified, isPersonal, isOwner, tenantId, user?.displayName]);

  if (!checks) return null;

  const done = checks.filter(c => c.done).length;
  const total = checks.length;
  const allDone = done === total;
  const pct = Math.round((done / total) * 100);

  if (allDone) return null;

  return (
    <>
      {/* Slim banner */}
      <div style={{
        background: B.white, border: `1px solid ${B.border}`,
        borderRadius: 12, padding: "12px 16px", marginBottom: 16,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}>
        {/* Progress ring area */}
        <div style={{
          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
          background: `conic-gradient(${B.red} ${pct * 3.6}deg, ${B.border} 0deg)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            width: 26, height: 26, borderRadius: "50%", background: B.white,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 800, color: B.redDark,
          }}>{pct}%</div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: B.redDark }}>
            Complete your setup — {done} of {total} steps done
          </div>
          <div style={{ fontSize: 12, color: B.steel, marginTop: 1 }}>
            Finish setting up your account to get the most from LastWish Box
          </div>
        </div>

        <button onClick={() => setOpen(true)} style={{
          background: B.red, border: "none", color: B.white,
          borderRadius: 8, padding: "8px 14px", fontSize: 13,
          fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
          boxShadow: `0 4px 12px rgba(152,0,0,0.20)`,
        }}>
          View steps
        </button>
      </div>

      {/* Modal */}
      {open && <Modal checks={checks} onClose={() => setOpen(false)} />}
    </>
  );
}
